<template>
  <header class="app-header">
    <div class="header-inner container">
      <!-- Logo -->
      <router-link to="/" class="logo">
        <div class="logo-icon">
          <span class="logo-qf">QF</span>
        </div>
        <div class="logo-text">
          <span class="logo-title">{{ locale === 'zh' ? '青岛自贸港货运网络' : 'Qingdao FTZ Chemical' }}</span>
        </div>
      </router-link>

      <!-- 桌面端导航 -->
      <nav class="nav-desktop">
        <router-link to="/" class="nav-link" exact>{{ $t('nav.home') }}</router-link>

        <a-dropdown :trigger="['hover']">
          <span class="nav-link nav-dropdown-trigger">
            {{ $t('nav.products') }} <a-icon type="down" />
          </span>
          <a-menu slot="overlay" class="product-dropdown">
            <a-menu-item key="all">
              <router-link to="/products">{{ $t('nav.allProducts') }}</router-link>
            </a-menu-item>
            <a-menu-divider />
            <a-menu-item v-for="cat in categories" :key="cat.key">
              <router-link :to="`/products?category=${cat.key}`">
                <a-icon :type="cat.icon" /> {{ locale === 'zh' ? cat.nameZh : cat.name }}
              </router-link>
            </a-menu-item>
          </a-menu>
        </a-dropdown>

        <router-link to="/about" class="nav-link">{{ $t('nav.about') }}</router-link>
        <router-link to="/contact" class="nav-link">{{ $t('nav.contact') }}</router-link>
      </nav>

      <!-- 右侧操作区 -->
      <div class="header-actions">
        <a-dropdown :trigger="['click']">
          <span class="lang-switch">
            <a-icon type="global" />
            <span>{{ locale === 'zh' ? '简体中文' : 'English' }}</span>
            <a-icon type="down" />
          </span>
          <a-menu slot="overlay" @click="switchLang">
            <a-menu-item key="en">
              <span>English</span>
              <a-icon v-if="locale === 'en'" type="check" style="margin-left:8px;color:#1ABC9C" />
            </a-menu-item>
            <a-menu-item key="zh">
              <span>简体中文</span>
              <a-icon v-if="locale === 'zh'" type="check" style="margin-left:8px;color:#1ABC9C" />
            </a-menu-item>
          </a-menu>
        </a-dropdown>

        <button class="header-cta" @click="openInquiry">
          {{ locale === 'zh' ? '获取报价' : 'Get Quote' }}
        </button>

        <!-- 移动端菜单按钮 -->
        <button class="mobile-menu-btn" @click="$emit('toggle-menu')">
          <a-icon :type="menuOpen ? 'close' : 'menu'" />
        </button>
      </div>
    </div>

    <!-- 搜索弹窗 -->
    <SearchModal :visible="showSearch" @close="showSearch = false" />
  </header>
</template>

<script>
import SearchModal from '@/components/common/SearchModal.vue'

export default {
  name: 'AppHeader',
  components: { SearchModal },
  props: {
    menuOpen: Boolean
  },
  data() {
    return {
      showSearch: false
    }
  },
  computed: {
    locale() {
      return this.$store.locale
    },
    categories() {
      return this.$store.categories
    }
  },
  methods: {
    switchLang({ key }) {
      this.$store.setLocale(key)
      this.$i18n.locale = key
    },
    openInquiry() {
      this.$root.$emit('open-inquiry', '')
    }
  }
}
</script>

<style lang="less" scoped>
@import '../../styles/variables.less';

.app-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: @header-height;
  background: @primary-dark;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.header-inner {
  display: flex;
  align-items: center;
  height: 100%;
  gap: 32px;
}

// Logo
.logo {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  text-decoration: none;

  &-icon {
    width: 36px;
    height: 36px;
    background: #fff;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;

    .logo-qf {
      font-size: 16px;
      font-weight: 800;
      color: @primary-dark;
    }
  }

  &-text {
    display: flex;
    flex-direction: column;
  }

  &-title {
    font-size: 16px;
    font-weight: 700;
    color: #fff;
    line-height: 1.2;
  }
}

// 桌面导航
.nav-desktop {
  display: flex;
  align-items: center;
  gap: 0;
  flex: 1;

  @media @mobile {
    display: none;
  }

  @media @tablet {
    display: none;
  }
}

.nav-link {
  padding: 8px 18px;
  color: rgba(255, 255, 255, 0.75);
  font-size: 14px;
  font-weight: 500;
  border-radius: 4px;
  transition: all @duration-fast @ease-professional;
  cursor: pointer;

  &:hover {
    color: #fff;
  }

  &.router-link-active {
    color: @accent-red;
  }
}

.nav-dropdown-trigger {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.product-dropdown {
  min-width: 200px;
  border-radius: 4px;
  box-shadow: @shadow-card-hover;

  .ant-dropdown-menu-item {
    padding: 8px 16px;

    a {
      display: flex;
      align-items: center;
      gap: 8px;
      color: @text-primary;
    }

    &:hover a {
      color: @primary-dark;
    }
  }
}

// 右侧操作
.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
}

.lang-switch {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  color: rgba(255, 255, 255, 0.75);
  cursor: pointer;
  font-size: 13px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all @duration-fast @ease-professional;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  @media @mobile {
    display: none;
  }
}

.header-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 20px;
  background: @accent-red;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all @duration-fast @ease-professional;

  &:hover {
    background: darken(@accent-red, 8%);
    transform: translateY(-1px);
  }

  @media @mobile {
    display: none;
  }
}

// 移动端菜单按钮
.mobile-menu-btn {
  display: none;
  background: none;
  border: none;
  color: #fff;
  font-size: 22px;
  cursor: pointer;
  padding: 4px;

  @media @mobile {
    display: block;
  }

  @media @tablet {
    display: block;
  }
}
</style>
